/**
 * Utilidades de texto de canción — formato de LÍNEAS ALTERNAS.
 * Espejo de backend/src/utils/songText.js (mantener ambos en sincronía).
 */
import { SectionType } from '../types';

const CHORD_TOKEN = /^[A-G][#b]?(?:maj|min|dim|aug|sus|add|m|M|\+|°|\d|#|b|\/[A-G][#b]?)*$/;
const CHORD_FILLER = /^(?:\||\|\||:\||\|:|%|x\d+|\d+x|\(|\)|\[|\]|\{|\}|-|–|—|,)$/i;
// Paréntesis, corchetes y llaves PEGADOS a un acorde («(C», «G)», «[Am», «D}»).
// Cada grupo los usa a su manera —repetir, pasaje opcional— y antes hacían que
// la línea entera pasara por letra: no se transponía ni se ocultaba.
const ENVOLTURA = /^[([{]+|[)\]}]+$/g;

/** ¿La línea contiene SOLO acordes (y separadores)? */
export const isChordLine = (line: string): boolean => {
  const trimmed = (line ?? '').trim();
  if (!trimmed) return false;

  const tokens = trimmed.split(/\s+/);
  let chords = 0;

  for (const token of tokens) {
    if (CHORD_FILLER.test(token)) continue;
    const nucleo = token.replace(ENVOLTURA, '');
    if (!nucleo || CHORD_FILLER.test(nucleo)) continue;
    if (!CHORD_TOKEN.test(nucleo)) return false;
    chords++;
  }

  return chords > 0;
};

/** ¿El contenido tiene al menos una línea de acordes? */
export const hasChords = (content: string): boolean =>
  (content ?? '').split(/\r?\n/).some(isChordLine);

/** Devuelve el contenido sin las líneas de acordes (modo "solo cantar"). */
export const stripChords = (content: string): string => {
  const lines = (content ?? '').split(/\r?\n/);
  const kept: string[] = [];

  for (const line of lines) {
    if (isChordLine(line)) continue;
    if (!line.trim() && (!kept.length || !kept[kept.length - 1].trim())) continue;
    kept.push(line);
  }

  while (kept.length && !kept[kept.length - 1].trim()) kept.pop();
  return kept.join('\n');
};

/** Contenido de la sección según el modo elegido. */
export const renderSection = (content: string, showChords: boolean): string =>
  showChords ? content ?? '' : stripChords(content);

/** Divide un texto pegado en bloques separados por líneas en blanco. */
export const splitIntoBlocks = (text: string): string[] =>
  (text ?? '')
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.replace(/^\n+|\n+$/g, ''))
    .filter((block) => block.trim().length > 0);

const deaccent = (s: string): string => (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Adivina el tipo de sección a partir de su encabezado ("Coro:", "[Puente]"). */
export const guessSectionType = (block: string): SectionType => {
  const firstLine = (block ?? '').split(/\r?\n/)[0] || '';
  const head = deaccent(firstLine).toLowerCase();

  if (/\bpre\s*-?\s*coro\b/.test(head)) return 'precoro';
  if (/\bcoro\b|\bchorus\b|\bestribillo\b/.test(head)) return 'coro';
  if (/\bpuente\b|\bbridge\b/.test(head)) return 'puente';
  if (/\bintro\b/.test(head)) return 'intro';
  if (/\bfinal\b|\boutro\b|\bcoda\b/.test(head)) return 'final';
  return 'estrofa';
};

/**
 * ¿La primera línea del bloque es solo un encabezado ("Coro:", "[Estrofa 2]")?
 * En ese caso el editor la usa como etiqueta y la quita del contenido.
 */
export const extractHeading = (block: string): { label: string | null; content: string } => {
  const lines = (block ?? '').split(/\r?\n/);
  const first = (lines[0] || '').trim();
  const esHeading =
    first.length > 0 &&
    first.length <= 30 &&
    !isChordLine(first) &&
    /^[[(]?\s*(estrofa|coro|pre\s*-?\s*coro|puente|intro|final|outro|coda|bridge|chorus|estribillo|verso)\b/i.test(
      deaccent(first)
    );

  if (!esHeading) return { label: null, content: block };

  return {
    label: first.replace(/^[[(]\s*|\s*[\])]$/g, '').replace(/[:.]\s*$/, '').trim(),
    content: lines.slice(1).join('\n')
  };
};
